<script setup lang="ts">
import { computed, watch } from 'vue';
import { useServiceCategoryFilterVisible } from '@/composables/useServiceCategoryFilterVisible';

interface Props {
  modelValue: number[];
  uncategorized: boolean;
  label?: string;
  variant?: 'underlined' | 'outlined' | 'plain' | 'solo' | 'filled';
  density?: 'default' | 'comfortable' | 'compact';
  maxVisibleChips?: number;
  hideDetails?: boolean | 'auto';
}

interface Emits {
  (e: 'update:modelValue', value: number[]): void;
  (e: 'update:uncategorized', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Categorías',
  variant: 'underlined',
  density: undefined,
  maxVisibleChips: 1,
  hideDetails: false,
});

const emit = defineEmits<Emits>();

// Pseudo-item for the "no category" bucket; real category ids are always positive.
const UNCATEGORIZED_VALUE = 0;

const { shouldShow, categories, serviceCategoriesEnabled } = useServiceCategoryFilterVisible();

// Drop persisted IDs the user can no longer see (feature disabled, archived category,
// or cross-company localStorage leakage). Without this the chips render blank and the
// API silently ignores a stale category.
watch([categories, serviceCategoriesEnabled], ([loaded, enabled]) => {
  if (!enabled) {
    if (props.modelValue.length) emit('update:modelValue', []);
    if (props.uncategorized) emit('update:uncategorized', false);
    return;
  }
  if (!loaded) return;
  const valid = new Set(loaded.map(category => category.id));
  const cleaned = props.modelValue.filter(id => valid.has(id));
  if (cleaned.length !== props.modelValue.length) {
    emit('update:modelValue', cleaned);
  }
}, { immediate: true });

const items = computed(() => [
  { value: UNCATEGORIZED_VALUE, title: 'Sin categoría' },
  ...(categories.value ?? []).map(category => ({ value: category.id, title: category.name })),
]);

const internalValue = computed({
  get: () => (props.uncategorized ?
    [UNCATEGORIZED_VALUE, ...props.modelValue] : props.modelValue),
  set: (value: number[]) => {
    emit('update:modelValue', value.filter(selected => selected !== UNCATEGORIZED_VALUE));
    emit('update:uncategorized', value.includes(UNCATEGORIZED_VALUE));
  },
});

const selectedCount = computed(() => internalValue.value.length);

function removeItem(value: number) {
  internalValue.value = internalValue.value.filter(selected => selected !== value);
}
</script>

<template>
  <v-autocomplete
    v-if="shouldShow"
    v-model="internalValue"
    :items="items"
    :label="label"
    :density="density"
    :variant="variant"
    :hide-details="hideDetails"
    multiple
    clearable
    single-line
  >
    <template #selection="{ item, index }">
      <v-chip
        v-if="index < maxVisibleChips"
        closable
        size="small"
        @click:close.stop="removeItem(item.value as number)"
      >
        {{ item.title }}
      </v-chip>
      <span
        v-else-if="index === maxVisibleChips"
        class="text-caption text-medium-emphasis ml-1"
      >
        +{{ selectedCount - maxVisibleChips }}
      </span>
    </template>
  </v-autocomplete>
</template>
